import { lazyWithRecovery } from "../../lib/chunk-load-recovery/lazy-with-recovery";
import type { RouteObject } from "react-router-dom";
import { Navigate, Outlet } from "react-router-dom";
import { LOGGED_HOME_PAGE_PATH, WELCOME_PATH } from "../../constants/env";
import { useIsLogged } from "../../hooks/auth/use-is-logged/use-is-logged";
import { useIsAdminRedirectActive } from "../../hooks/feature-flags/useIsAdminRedirectActive/useIsAdminRedirectActive";

import { AdminRedirectGuard } from "../../app/router/guards/components/AdminRedirectGuard";
import { ADMIN_ABAC_PERMISSION } from "../../app/router/guards/constants/admin-abac-permission.const";
import { createAbacLoader } from "../../app/router/guards/loaders/abac.loader";
import { accountUpdateLoader } from "../../app/router/guards/loaders/account-update.loader";
import { createAuthLoader } from "../../app/router/guards/loaders/auth.loader";
import { combineLoaders } from "../../app/router/guards/loaders/composite.loader";
import { BaseLayout } from "../../components/layouts/BaseLayout";
import { BaseLayoutV2 } from "../../components/layouts/BaseLayoutV2";
import DefaultLayout from "../../components/layouts/default-layout/DefaultLayout";
import RouteWrapper from "../../components/layouts/route-wrapper/RouteWrapper";
import { INVITE_IN_BONUSX, PRICING_PAGE_SEO_TITLE, PRICING_PAGE_TITLE } from "../../constants/commonStrings";
import {
  ARCHIVE_PATH,
  DASHBOARD_PATH,
  INVITE_FRIENDS_PATH,
  PRICING_PAGE_PATH,
  PRODUCT_REQUISITES_CONFIRMED_PATH,
  SERVICES_PATH,
  UPGRADE_PATH,
} from "../../constants/env";
import { RouteWrapperVariant } from "../../constants/layouts/route-wrapper-variant.enum";
import DynamicPaywallRedirect from "../../features/pricing/components/DynamicPaywallRedirect/DynamicPaywallRedirect";
import { ActiveCampaignChatHandler } from "../../legacy/components/ActiveCampaignChatHandler";
import CloseChatbotPanelWrapper from "../../legacy/components/Chatbot/wrappers/close-chatbot-panel-wrapper/close-chatbot-panel-wrapper";
import { HeaderModalBar } from "../../legacy/components/HeaderModalBar/header-modal-bar.component";
import { PaymentBackdrop } from "../../legacy/components/PaymentBackdrop/payment-backdrop";
import SEO from "../../legacy/components/Seo";
import { iseeRedirectMapping } from "./isee-redirect";
import { createProfileRoutes, createProfileRoutesDefaultLayoutOutlet } from "./profile-routes";
import { createQuestionnaireRoutes } from "./questionnaire-routes";
import { createRequestedServicesRoutes } from "./requested-services-routes";
import { createSettingsRoutes } from "./settings-routes";
import { withSuspense } from "./utils/with-suspense";

// Lazy components
const AdminRoutes = lazyWithRecovery(() => import("../admin-routes"));
const ArchivePage = lazyWithRecovery(() => import("../../legacy/pages/ArchivePage"));
const UploadToArchivePage = lazyWithRecovery(() => import("../../legacy/pages/ArchivePage/UploadDocumentToArchive"));
const CheckoutSuccessPage = lazyWithRecovery(() => import("../../legacy/pages/CheckoutSuccessPage"));
const InviteFriendsPage = lazyWithRecovery(() => import("../../legacy/pages/InviteFriendsPage/InviteFriendsPage"));
const NotificationPageNew = lazyWithRecovery(() => import("../../legacy/pages/NotificationPage/NotificationPageNew"));
const ProductsListPage = lazyWithRecovery(() => import("../../legacy/pages/ProductsListPage/ProductsListPage"));
const PricingPage = lazyWithRecovery(() => import("../../legacy/pages/pricing-page/pricing-page"));
const Logout = lazyWithRecovery(() => import("./logout-routes"));
const ProductPageDetail = lazyWithRecovery(() => import("../../app/pages/products/ProductPageDetail"));
const RequisitesConfirmedPage = lazyWithRecovery(
  () => import("../../app/pages/RequisitesConfirmedPage/RequisitesConfirmedPage")
);
const RedirectToProductByUniqueId = lazyWithRecovery(
  () =>
    import(
      "../../legacy/pages/ProductPageDetail/components/redirect-to-product-by-unique-id/redirect-to-product-by-unique-id"
    )
);

function LoggedInGuard(): JSX.Element {
  const isLogged = useIsLogged();
  const redirectTo = isLogged ? LOGGED_HOME_PAGE_PATH : WELCOME_PATH;
  return <Navigate to={redirectTo} replace />;
}

function AdminRouteEntry(): JSX.Element {
  const isAdminRedirectActive = useIsAdminRedirectActive();

  if (isAdminRedirectActive) {
    return <AdminRedirectGuard />;
  }

  return (
    <>
      <SEO title="Admin" />
      <RouteWrapper>{withSuspense(<AdminRoutes />)}</RouteWrapper>
    </>
  );
}

export function createProtectedCitizenRoutes(): RouteObject[] {
  return [...createProtectedRoutes(), { index: true, element: <LoggedInGuard /> }];
}

function isNaspiPage() {
  return window.location.pathname.endsWith("services/16");
}

const productDetailAllowedIf = { can: "update", model: "Account" } as const;

function createProtectedRoutes(): RouteObject[] {
  return [
    {
      path: "/admin/*",
      loader: combineLoaders(
        createAuthLoader(),
        createAbacLoader({ permission: ADMIN_ABAC_PERMISSION, redirectTo: "/" })
      ),
      element: <AdminRouteEntry />,
    },
    ...iseeRedirectMapping.map(({ from, to }) => ({
      path: `${SERVICES_PATH}/${from}`,
      element: <Navigate to={`${SERVICES_PATH}/${to}`} replace />,
    })),
    ...createQuestionnaireRoutes(),
    ...createRequestedServicesRoutes(),
    ...createProfileRoutes(),
    ...createSettingsRoutes(),
    {
      path: "/checkout-success/:transactionId/:productId",
      loader: accountUpdateLoader,
      element: (
        <>
          <SEO title="Checkout completato" />
          <BaseLayout>{withSuspense(<CheckoutSuccessPage />)}</BaseLayout>
        </>
      ),
    },
    {
      path: "/upload-to-archive",
      loader: accountUpdateLoader,
      element: (
        <>
          <SEO title="Carica documento" />
          <BaseLayoutV2 header={<HeaderModalBar title="Carica documento" />}>
            <CloseChatbotPanelWrapper>{withSuspense(<UploadToArchivePage />)}</CloseChatbotPanelWrapper>
          </BaseLayoutV2>
        </>
      ),
    },
    {
      path: "/logout",
      element: <CloseChatbotPanelWrapper>{withSuspense(<Logout />)}</CloseChatbotPanelWrapper>,
    },
    {
      path: DASHBOARD_PATH,
      loader: combineLoaders(
        createAuthLoader(),
        createAbacLoader({ permission: { can: "readOne", model: "Account" } })
      ),
      element: <Navigate to={LOGGED_HOME_PAGE_PATH} replace />,
    },
    {
      path: "/",
      element: (
        <DefaultLayout>
          <Outlet />
        </DefaultLayout>
      ),
      children: [
        ...createProfileRoutesDefaultLayoutOutlet(),
        {
          path: SERVICES_PATH,
          loader: accountUpdateLoader,
          element: (
            <>
              <SEO title="Bonus" />
              <RouteWrapper>
                <PaymentBackdrop>{withSuspense(<ProductsListPage />)}</PaymentBackdrop>
              </RouteWrapper>
            </>
          ),
        },
        {
          path: `${SERVICES_PATH}/by-uid/:uniqueIdentifier`,
          loader: createAbacLoader({ permission: productDetailAllowedIf }),
          element: withSuspense(<RedirectToProductByUniqueId />),
        },
        {
          path: `${SERVICES_PATH}/:id`,
          loader: createAbacLoader({ permission: productDetailAllowedIf }),
          element: (
            <>
              <ActiveCampaignChatHandler condition={isNaspiPage} />
              <RouteWrapper variant={RouteWrapperVariant.productDetail}>
                <PaymentBackdrop>{withSuspense(<ProductPageDetail />)}</PaymentBackdrop>
              </RouteWrapper>
            </>
          ),
        },
        {
          path: `${ARCHIVE_PATH}/*`,
          loader: accountUpdateLoader,
          element: (
            <>
              <SEO title="Documenti personali" />
              <RouteWrapper useLegacyDesign>{withSuspense(<ArchivePage />)}</RouteWrapper>
            </>
          ),
        },
        {
          path: INVITE_FRIENDS_PATH,
          loader: accountUpdateLoader,
          element: (
            <>
              <SEO title={INVITE_IN_BONUSX} />
              <RouteWrapper>{withSuspense(<InviteFriendsPage />)}</RouteWrapper>
            </>
          ),
        },
        {
          path: UPGRADE_PATH,
          element: <Navigate to={PRICING_PAGE_PATH} replace />,
        },
        {
          path: PRICING_PAGE_PATH,
          loader: accountUpdateLoader,
          element: (
            <DynamicPaywallRedirect>
              <SEO title={PRICING_PAGE_SEO_TITLE} />
              <RouteWrapper variant={RouteWrapperVariant.fullScreenWithHeaderAndCloseButton} title={PRICING_PAGE_TITLE}>
                <PaymentBackdrop>
                  <PricingPage />
                </PaymentBackdrop>
              </RouteWrapper>
            </DynamicPaywallRedirect>
          ),
        },
        {
          path: "/notifications",
          loader: accountUpdateLoader,
          element: (
            <>
              <SEO title="Notifiche" />
              <RouteWrapper
                variant={RouteWrapperVariant.secondLevelWithTitleAndAiAssistant}
                defaultBackButtonUrl={LOGGED_HOME_PAGE_PATH}
                title="Notifiche"
              >
                {withSuspense(<NotificationPageNew />)}
              </RouteWrapper>
            </>
          ),
        },
        {
          path: PRODUCT_REQUISITES_CONFIRMED_PATH,
          element: (
            <RouteWrapper variant={RouteWrapperVariant.fullScreenBluexBgWithCloseButton}>
              <PaymentBackdrop>{withSuspense(<RequisitesConfirmedPage />)}</PaymentBackdrop>
            </RouteWrapper>
          ),
        },
      ],
    },
    { path: "*", element: <Navigate to={LOGGED_HOME_PAGE_PATH} replace /> },
  ];
}
